// Security panel shared by talent/agency settings hubs (dark variant aware).
// Password changes use the audited email reset flow (no in-app endpoint).
import React, { useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Loader2, MailCheck } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { HubCard, IconChip } from './HubKit';

export const SecurityPanel = () => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const sendReset = async () => {
    if (!user?.email) return;
    setSending(true);
    try {
      await api.post('/auth/forgot-password', { email: user.email });
      setSent(true);
      toast.success('Password reset email sent', {
        description: `Check ${user.email} for a secure link to set a new password.`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send reset email');
    } finally {
      setSending(false);
    }
  };

  return (
    <HubCard title="Password" sub="Keep your account locked down." testid="settings-security-panel">
      <div className="flex items-start gap-3.5">
        <IconChip icon={KeyRound} accent="purple" />
        <div className="flex-1 space-y-3">
          <p className="text-sm text-slate-300">
            For your security, password changes go through a verified email link
            sent to <span className="font-semibold text-slate-100">{user?.email}</span>.
          </p>
          <button
            type="button"
            onClick={sendReset}
            disabled={sending || sent}
            data-testid="security-send-reset-btn"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all
              active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70
              ${sent
                ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 cursor-default'
                : 'bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950 hover:shadow-lg hover:shadow-cyan-500/25 disabled:opacity-60'}`}
          >
            {sending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : sent
                ? <MailCheck className="w-4 h-4" />
                : <KeyRound className="w-4 h-4" />}
            {sent ? 'Reset link sent — check your email' : 'Email me a password reset link'}
          </button>
        </div>
      </div>
    </HubCard>
  );
};
